import { formatNumber } from '~/utils/numberFormatters';

import type { AssetField } from '../../Assets/AssetFieldGrid';
import { formatSyncedDate, formatSyncedDay } from '../../Assets/utils';

import type { AccountDetailsField } from './accountDetailsLayout';
import type {
  AccountDetails,
  AccountDetailsAddress,
  AccountDetailsRecord,
  AccountDetailsUser,
  AccountDetailsValue,
} from './types';

type AccountDetailsFieldView = AssetField & {
  // Set for Account lookups, which open that Account's page in Admin.
  accountId?: string;
  // Rendered with ContactMethodLink, because the grid opens every href in a new tab.
  phone?: string;
};

function readDetailValue(details: AccountDetails, field: AccountDetailsField): AccountDetailsValue {
  return field.section ? (details[field.section][field.key] ?? null) : details.external_refs.salesforce;
}

function isNamed(value: AccountDetailsValue): value is AccountDetailsUser | AccountDetailsRecord {
  return typeof value === 'object' && value !== null && 'name' in value;
}

function isAddress(value: AccountDetailsValue): value is AccountDetailsAddress {
  return typeof value === 'object' && value !== null && 'street' in value;
}

// Salesforce URL fields often hold a bare domain, which as an href would resolve inside Admin.
function externalUrl(text: string) {
  if (/^https?:\/\//i.test(text)) {
    return text;
  }
  return /^[a-z][a-z\d+.-]*:/i.test(text) ? undefined : `https://${text}`;
}

function addressLines(address: AccountDetailsAddress) {
  const cityLine = [address.city, address.state, address.postal_code].filter(Boolean).join(' ');
  return [address.street, cityLine, address.country].filter(Boolean).join('\n');
}

export function toDetailFieldView(details: AccountDetails, field: AccountDetailsField): AccountDetailsFieldView {
  const { label, kind } = field;
  const value = readDetailValue(details, field);

  if (kind === 'check') {
    return { label, checkbox: typeof value === 'boolean' ? value : null };
  }
  if (value === null || value === '') {
    return { label };
  }

  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;

  switch (kind) {
    case 'number':
      // Salesforce percent fields hold small fractions (0.131), so two decimals would round them away.
      return { label, value: typeof value === 'number' ? formatNumber(value, 4, { pad: false }) : text };
    case 'date':
      return { label, value: formatSyncedDay(text) };
    case 'datetime':
      return { label, value: formatSyncedDate(text) };
    case 'list':
      return { label, value: Array.isArray(value) ? value.join('; ') : text };
    case 'person':
    case 'record':
      return { label, value: isNamed(value) ? value.name : undefined };
    case 'account':
      return isNamed(value) && 'external_refs' in value
        ? { label, value: value.name, accountId: value.external_refs.salesforce }
        : { label };
    case 'link':
      return { label, value: text, href: text && externalUrl(text) };
    case 'phone':
      return { label, value: text, phone: text };
    case 'address':
      return { label, value: isAddress(value) ? addressLines(value) : undefined };
    default:
      return { label, value: text };
  }
}
